import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import SettingServices from "../../../services/SettingServices";

/**
 * The five free-text notes shown around the apps. Each saves on its own button,
 * matching the previous behaviour. Initial values come from the settings row
 * that Settings.js already fetched, so this tab does not re-request it.
 */
export default function NotesSettings({ settingData, onSaved }) {
  const settingApi = new SettingServices();

  const [loginNotes, setLoginNotes] = useState("");
  const [homeNotes, setHomeNotes] = useState("");
  const [liveSessionNote1, setLiveSessionNote1] = useState("");
  const [liveSessionNote2, setLiveSessionNote2] = useState("");
  const [winningSlipNote, setWinningSlipNote] = useState("");

  const [loginNotesLoading, setLoginNotesLoading] = useState(false);
  const [homeNotesLoading, setHomeNotesLoading] = useState(false);
  const [liveSessionNotesLoading, setLiveSessionNotesLoading] = useState(false);
  const [winningSlipNoteLoading, setWinningSlipNoteLoading] = useState(false);

  useEffect(() => {
    if (!settingData) return;
    setLoginNotes(settingData.login_notes || "");
    setHomeNotes(settingData.home_notes || "");
    setLiveSessionNote1(settingData.live_session_note1 || "");
    setLiveSessionNote2(settingData.live_session_note2 || "");
    setWinningSlipNote(settingData.winning_slip_note || "");
  }, [settingData]);

  const handleSaveLoginNotes = () => {
    setLoginNotesLoading(true);
    settingApi
      .UpdateLoginNotes({ login_notes: loginNotes })
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message || "Login notes saved successfully");
          if (onSaved) onSaved();
        }
      })
      .catch((error) => {
        console.error("Error saving login notes:", error);
        toast.dismiss();
        toast.error("Failed to save login notes");
      })
      .finally(() => {
        setLoginNotesLoading(false);
      });
  };

  const handleSaveHomeNotes = () => {
    setHomeNotesLoading(true);
    settingApi
      .UpdateHomeNotes({ home_notes: homeNotes })
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message || "Home notes saved successfully");
          if (onSaved) onSaved();
        }
      })
      .catch((error) => {
        console.error("Error saving home notes:", error);
        toast.dismiss();
        toast.error("Failed to save home notes");
      })
      .finally(() => {
        setHomeNotesLoading(false);
      });
  };

  const handleSaveLiveSessionNotes = (note) => {
    setLiveSessionNotesLoading(true);
    settingApi
      .UpdateLiveSessionNotes({
        note,
        live_session_notes:
          note === "note1" ? liveSessionNote1 : liveSessionNote2,
      })
      .then((response) => {
        if (response.isSuccess) {
          toast.success(
            response.message || "Live session notes saved successfully",
          );
          if (onSaved) onSaved();
        }
      })
      .catch((error) => {
        console.error("Error saving live session notes:", error);
        toast.dismiss();
        toast.error("Failed to save live session notes");
      })
      .finally(() => {
        setLiveSessionNotesLoading(false);
      });
  };

  const handleSaveWinningSlipNote = () => {
    setWinningSlipNoteLoading(true);
    settingApi
      .UpdateLiveSessionNotes({
        note: "winning_slip",
        live_session_notes: winningSlipNote,
      })
      .then((response) => {
        if (response.isSuccess) {
          toast.success(
            response.message || "Winning slip note saved successfully",
          );
          if (onSaved) onSaved();
        }
      })
      .catch((error) => {
        console.error("Error saving winning slip note:", error);
        toast.dismiss();
        toast.error("Failed to save winning slip note");
      })
      .finally(() => {
        setWinningSlipNoteLoading(false);
      });
  };

  return (
    <>
      <h5 className="settings-section-title">Notes</h5>
      <p className="settings-section-sub">
        Login screen, home screen, live session ticker, and the printed winning
        slip. Each note saves on its own.
      </p>

      <div>
        <label className="setting-input-label">Login Notes</label>
        <div className="setting-input-box">
          <textarea
            placeholder="Enter login notes"
            value={loginNotes}
            onChange={(e) => setLoginNotes(e.target.value)}
            rows={5}
            className="setting-input-textarea"
          />
          <button
            className="setting-save-notes-btn"
            onClick={handleSaveLoginNotes}
            disabled={loginNotesLoading}
          >
            {loginNotesLoading ? "Saving..." : "Save Login Notes"}
          </button>
        </div>
      </div>

      <div>
        <label className="setting-input-label">Home Notes</label>
        <div className="setting-input-box">
          <textarea
            placeholder="Enter home notes"
            value={homeNotes}
            onChange={(e) => setHomeNotes(e.target.value)}
            rows={5}
            className="setting-input-textarea"
          />
          <button
            className="setting-save-notes-btn"
            onClick={handleSaveHomeNotes}
            disabled={homeNotesLoading}
          >
            {homeNotesLoading ? "Saving..." : "Save Home Notes"}
          </button>
        </div>
      </div>

      <div>
        <label className="setting-input-label">Live Session Note1</label>
        <div className="setting-input-box">
          <textarea
            placeholder="Enter live session note1"
            value={liveSessionNote1}
            onChange={(e) => setLiveSessionNote1(e.target.value)}
            rows={3}
            className="setting-input-textarea"
          />
          <button
            className="setting-save-notes-btn"
            onClick={() => handleSaveLiveSessionNotes("note1")}
            disabled={liveSessionNotesLoading}
          >
            {liveSessionNotesLoading ? "Saving..." : "Save Live Session Note1"}
          </button>
        </div>
      </div>

      <div>
        <label className="setting-input-label">Live Session Note2</label>
        <div className="setting-input-box">
          <textarea
            placeholder="Enter live session note2"
            value={liveSessionNote2}
            onChange={(e) => setLiveSessionNote2(e.target.value)}
            rows={3}
            className="setting-input-textarea"
          />
          <button
            className="setting-save-notes-btn"
            onClick={() => handleSaveLiveSessionNotes("note2")}
            disabled={liveSessionNotesLoading}
          >
            {liveSessionNotesLoading ? "Saving..." : "Save Live Session Note2"}
          </button>
        </div>
      </div>

      <div>
        <label className="setting-input-label">Winning Slip Note</label>
        <div className="setting-input-box">
          <textarea
            placeholder="Enter winning slip note"
            value={winningSlipNote}
            onChange={(e) => setWinningSlipNote(e.target.value)}
            rows={3}
            className="setting-input-textarea"
          />
          <button
            className="setting-save-notes-btn"
            onClick={handleSaveWinningSlipNote}
            disabled={winningSlipNoteLoading}
          >
            {winningSlipNoteLoading ? "Saving..." : "Save Winning Slip Note"}
          </button>
        </div>
      </div>
    </>
  );
}
